import { useEffect } from 'react';
import type { AttentionLogin, HomeSummary, ServiceConnection } from '../../../services/src/shared/api-types.ts';
import type { ScreenProps } from '../App.tsx';
import { body, headline, isFailure } from '../activity.ts';
import { clock, maskLabel, money, moneyWhole, monthDay, num, plural, relativeStamp } from '../format.ts';
import { Empty, ErrorBanner, Loading, Stat } from '../ui.tsx';

const SERVICE_NAMES: Record<string, string> = { salesforce: 'Salesforce', xero: 'Xero', wise: 'Wise' };
const SERVICE_STATES: Record<string, string> = { ok: 'connected', not_configured: 'not set up', error: 'failing', pending: 'waiting', login_required: 'needs sign-in' };

export function Home({ summary, summaryError, refreshSummary, isAdmin }: ScreenProps) {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  // The shell loads the summary at sign-in; coming back to Home re-reads it.
  useEffect(() => {
    if (summary) refreshSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) {
    return (
      <div className="page wide">
        <header className="page-head">
          <div>
            <h1>{today}</h1>
          </div>
        </header>
        {summaryError ? <ErrorBanner message={summaryError} onRetry={refreshSummary} /> : <Loading kind="home" />}
      </div>
    );
  }

  const { review, unmatched, connections, lastSync } = summary;
  const payNote =
    unmatched.payments.checksOver30 > 0
      ? `${plural(unmatched.payments.checksOver30, 'check')} over 30 days`
      : unmatched.payments.over30 > 0
        ? `${num(unmatched.payments.over30)} older than 30 days`
        : 'None older than 30 days';

  return (
    <div className="page wide">
      <header className="page-head">
        <div>
          <h1>{today}</h1>
          <p className="lede">
            {!lastSync
              ? 'No sync has run yet. The service syncs every day at 5am.'
              : lastSync.ok
                ? `Last sync ${clock(lastSync.at)}. Syncs every day at 5am.`
                : `The last sync, at ${clock(lastSync.at)}, failed. The next one runs at 5am.`}
          </p>
        </div>
      </header>

      {summaryError && <ErrorBanner message={`Showing earlier numbers. ${summaryError}`} onRetry={refreshSummary} />}
      {lastSync && !lastSync.ok && (
        <div className="banner error" role="alert">
          <div>
            <strong>The last sync failed</strong>
            <span>{lastSync.summary}</span>
          </div>
        </div>
      )}
      {connections.attention.map((a) => (
        <AttentionBanner key={a.id} login={a} isAdmin={isAdmin} />
      ))}

      <div className="stats">
        <Stat
          label="Ready for review"
          value={num(review.count)}
          note={review.count ? `${moneyWhole(review.total)} waiting` : 'Nothing waiting'}
          accent={review.count > 0}
          href="#/review"
        />
        <Stat
          label="Bank lines with no payment"
          value={num(unmatched.lines.count)}
          note={unmatched.asOf ? `${num(unmatched.lines.over30)} older than 30 days` : 'No matching run yet'}
          href="#/unmatched/lines"
        />
        <Stat label="Payments not cleared" value={num(unmatched.payments.count)} note={unmatched.asOf ? payNote : 'No matching run yet'} href="#/unmatched/payments" />
        <Stat label="Matched automatically" value={num(summary.autoMatchedThisMonth)} note="this month" />
      </div>

      <div className="columns">
        <section className="card" aria-labelledby="home-review">
          <div className="card-head">
            <h2 id="home-review">Ready for review</h2>
            {review.count > 0 && <a href="#/review">Review all {num(review.count)}</a>}
          </div>
          {review.top.length === 0 ? (
            <p className="empty-row">Nothing waiting for review.</p>
          ) : (
            <ul className="rows">
              {review.top.map((t) => (
                <li key={t.id}>
                  <a className="row" href="#/review">
                    <span className="row-main">
                      <strong>{t.title}</strong>
                      <span className="row-sub">
                        {[t.alternatives > 1 ? `Held check, ${t.alternatives} possible payments` : t.reason, t.trip].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="num">{money(t.amount)}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card" aria-labelledby="home-activity">
          <div className="card-head">
            <h2 id="home-activity">Recent activity</h2>
            <a href="#/activity">All activity</a>
          </div>
          {summary.activity.length === 0 ? (
            <p className="empty-row">Nothing yet.</p>
          ) : (
            <ol className="rows">
              {summary.activity.map((e) => (
                <li key={e.id} className="row">
                  <time className="row-time" dateTime={e.at}>
                    {relativeStamp(e.at)}
                  </time>
                  <span className="row-main">
                    <span>
                      <strong className={isFailure(e) ? 'st-err' : undefined}>{headline(e)}</strong>
                    </span>
                    <span className="row-sub">{body(e)}</span>
                  </span>
                  {e.amount !== undefined && <span className="num">{money(e.amount)}</span>}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      <section className="card health" aria-labelledby="home-connections">
        <div>
          <h2 id="home-connections">Connections</h2>
          <span className="small muted">
            {connections.totals.accounts
              ? `${num(connections.totals.syncing)} of ${plural(connections.totals.accounts, 'account')} syncing`
              : 'No bank accounts connected yet'}
            {servicesLine(connections.services) && ` · ${servicesLine(connections.services)}`}
          </span>
        </div>
        {(connections.attention.length > 0 || connections.notConnected.length > 0) && (
          <div className="chips">
            {connections.attention.map((a) => (
              <span key={a.id} className="chip warn">
                {a.institution}: {a.needsSignIn ? 'sign in again' : a.error ? 'not syncing' : a.newAccountsAt ? 'new account to add' : 'not syncing'}
              </span>
            ))}
            {connections.notConnected.map((n) => (
              <span key={n.id} className="chip flat">
                {n.institution}: not connected
              </span>
            ))}
          </div>
        )}
        {isAdmin && <a href="#/connections">View all accounts</a>}
      </section>
    </div>
  );
}

function AttentionBanner({ login, isAdmin }: { login: AttentionLogin; isAdmin: boolean }) {
  const names = login.accounts.map((a) => `${a.name} ${maskLabel(a.mask)}`.trim());
  const which = names.length === 0 ? 'this login' : names.length <= 2 ? names.join(' and ') : `${names[0]} and ${names.length - 1} more accounts`;
  if (!login.needsSignIn && !login.error && login.newAccountsAt) {
    return (
      <div className="banner" role="status">
        <div>
          <strong>{login.institution} has a new account to add</strong>
          <span>
            Often a new or reissued card. Its charges come in once it's shared in Manage accounts.
            {!isAdmin && ' An admin can add it.'}
          </span>
        </div>
        {isAdmin && (
          <a className="btn btn-dark" href="#/connections">
            Manage accounts
          </a>
        )}
      </div>
    );
  }
  return (
    <div className="banner" role="alert">
      <div>
        <strong>{login.needsSignIn ? `${login.institution} needs you to sign in again` : `${login.institution} isn't syncing`}</strong>
        <span>
          {login.since ? `Lines from ${which} stopped coming in on ${monthDay(login.since)}.` : `No lines have come in from ${which} yet.`}
          {!login.needsSignIn && login.error ? ` ${login.error}` : ''}
          {!isAdmin && ' An admin can reconnect it.'}
        </span>
      </div>
      {isAdmin && (
        <a className="btn btn-dark" href="#/connections">
          {login.needsSignIn ? 'Reconnect' : 'View'}
        </a>
      )}
    </div>
  );
}

/** "Salesforce and Xero connected", or each one's state when they differ. */
function servicesLine(services: HomeSummary['connections']['services']): string {
  const pick = (name: ServiceConnection['name']) => services.find((s) => s.name === name);
  const shown = [pick('salesforce'), pick('xero')].filter((s): s is ServiceConnection => Boolean(s));
  if (!shown.length) return '';
  if (shown.every((s) => s.status === shown[0].status)) {
    return `${shown.map((s) => SERVICE_NAMES[s.name]).join(' and ')} ${SERVICE_STATES[shown[0].status] ?? shown[0].status}`;
  }
  return shown.map((s) => `${SERVICE_NAMES[s.name]} ${SERVICE_STATES[s.status] ?? s.status}`).join(', ');
}
